import { ApiError } from "@/lib/api-client";
import {
  CONTRACT_BLOCKED_BODY,
  CONTRACT_INACTIVE_TITLE,
} from "@/routes/org-admin/contract-notice";
import { INVALID_SIGN_IN_EMAIL_BODY } from "@/routes/org-admin/sign-in-email-notice";

// Maps a failed invitation POST to a UI outcome (Story 14.2, FR75). Mirror of
// seat-request-error.ts + client-error.ts: we branch ONLY on the structured
// `error.code` / `error.status` and the 422 `loc` — NEVER on the server's raw
// `msg`/`input`. Deterministic 4xx account/data states get calm, dedicated
// branches instead of a generic retry (conventions lesson #8: retry must never
// be the only answer to a state that is a property of the account/seat pool).
//
// [Source: src/application/organizations/invite_clinician/* — 409 SEAT-001
//  no_seats_available; 409 SEAT-002 seat_write_conflict with a structured
//  `details.reason` (already_seated | capacity_full | duplicate_email); 409
//  ORG-002 no_professional_organization; 422 schema validation; 422 ORG-015
//  invalid_email_format with a structured `details` ({reason, message}) — the
//  sign-in-grammar refusal the change-email slice raises under the SAME code;
//  429 rate limit (sliding window); 403 ORG-010 contract_expired (Story 14.12);
//  403 RBAC.]
// Each coded 409 is a DISTINCT account/seat state,
// so we branch on `code` + `details.reason` — collapsing them all to
// "already seated" mis-reports a capacity race, an org-config problem, or a
// duplicate account (conventions lesson #8).

export interface InvitationBanner {
  /** `info` = a settled account/seat state (no retry); `warn`/`danger` = check/transport. */
  variant: "info" | "warn" | "danger";
  title: string;
  body: string;
  /**
   * True when the banner's remedy is the Request-additional-seats CTA (14.7)
   * — the screen renders the dashboard link beside the copy. Set for the
   * no-seats states (SEAT-001 / capacity race) AND the ORG-010 contract
   * refusal: the latter's body names the CTA, and on the stale-read path this
   * banner is the only surface telling the admin what to do.
   */
  seatsCta?: boolean;
}

/** Inline messages keyed to the form's RHF field names (422 field errors). */
export interface InvitationFieldErrors {
  firstName?: string;
  lastName?: string;
  workEmail?: string;
  organizationRoleLabel?: string;
}

export interface MappedInvitationError {
  /** Banner descriptor, or null when the failure is fully handled inline. */
  banner: InvitationBanner | null;
  /** Per-field messages to attach via `form.setError` without dropping form state. */
  fieldErrors: InvitationFieldErrors;
}

const GENERIC: InvitationBanner = {
  variant: "danger",
  title: "Couldn't send the invitation",
  body: "Something went wrong sending the invitation. Please try again.",
};

// SEAT-001 — the seat pool is exhausted. A settled state, not a transport
// failure: renewal is org-admin/CITS scope via the Request-additional-seats CTA
// (Story 14.7), so the copy directs there rather than offering a dead retry.
const NO_SEATS: InvitationBanner = {
  variant: "info",
  title: "No seats available",
  body: "This organization has no remaining seats, so the invitation wasn't sent. Use the Request additional seats action on your dashboard to ask CITS for more.",
  seatsCta: true,
};

// SEAT-002 already_seated (or any unrecognized 409) — the invitee already holds
// an active seat. Friendly, deterministic; no retry loop.
const ALREADY_SEATED: InvitationBanner = {
  variant: "warn",
  title: "This person already holds a seat",
  body: "This clinician is already assigned to a seat, so no new invitation was sent. Check your roster on the dashboard.",
};

// SEAT-002 duplicate_email (Story 22.4, AC16) — this address already has an AMPS
// account. Two parts, because the refusal has two halves and they belong to
// different places on the form.
//
// The FIELD marker says what is wrong with the value the admin typed. It stays,
// because the address is what the server refused and the field is where the
// admin is looking.
//
// The BANNER carries the route, and it is new. Until 22.4 this refusal was
// inline-only, which quietly told the admin the fix was to edit the address —
// but if the intended person really does hold an AMPS account, there is no
// address that works, and retyping is the one thing that cannot help. Placing an
// existing account on an organization seat is a CITS super_admin action (FR88),
// so the org_admin REQUESTS it rather than performs it, and the copy has to say
// so or the screen is a dead end.
//
// **The route sentence is conditional on purpose, and that is a disclosure
// constraint, not a style choice.** The server's `duplicate_email` says exactly
// one thing: an account exists at this address. It does not say the holder is a
// clinician, or what their subscription is, or whether they are seated
// somewhere else. "If this is a clinician who already holds an AMPS account…"
// offers the path without asserting any of that; the rejected phrasing ("this
// clinician already has an individual subscription; ask CITS to attach them")
// asserts two facts the refusal never disclosed (SCP §4F risk 4, AC17).
//
// `seatsCta` is deliberately unset: the remedy is not more seats.
export const DUPLICATE_EMAIL_FIELD_MESSAGE =
  "An account with this work email already exists.";

const EXISTING_ACCOUNT: InvitationBanner = {
  variant: "info",
  title: "This address already has an AMPS account",
  body: "No invitation was sent, and an account can't be created again for this address. If this is a clinician who already holds an AMPS account and should be working in your organization, ask CITS to place their existing account on one of your seats — that isn't something this screen can do.",
};

// ORG-002 — the actor's organization claim does not resolve to a professional
// org, so there is no seat pool to invite into. A settled account/config state
// (not a transport failure): no retry, and no seats CTA (the fix is org setup,
// not more seats).
const ORG_NOT_READY: InvitationBanner = {
  variant: "warn",
  title: "Organization not ready",
  body: "Your organization isn't set up to invite clinicians yet. Contact CITS support if you believe this is an error.",
};

// 429 — the endpoint carries a sliding-window rate limiter (human-facing email
// side-effect). Reassure rather than alarm; the CTA stays available to retry
// later, so no dead button.
const RATE_LIMITED: InvitationBanner = {
  variant: "info",
  title: "You're sending invitations quickly",
  body: "You've sent several invitations in a short window. Please wait a little while before sending another.",
};

// ORG-010 (Story 14.12) — the organization's contract is not active, so the
// backend refused the write. A settled COMMERCIAL state, not a permission
// problem: the generic 403 copy ("your account isn't permitted") would send an
// org_admin to argue about their own role when the fix is a contract renewal.
// Copy comes from the shared const so this refusal reads identically to the
// dashboard banner the same admin sees. The body names the seat-request CTA,
// so the banner renders it too: on the stale-read path (dashboard said
// `active`, contract lapsed mid-session) this is the admin's ONLY banner, and
// naming a way out without rendering it would be a dead instruction.
const CONTRACT_INACTIVE: InvitationBanner = {
  variant: "warn",
  title: CONTRACT_INACTIVE_TITLE,
  body: CONTRACT_BLOCKED_BODY,
  seatsCta: true,
};

// 403 — the actor lost the capability (or is not an org_admin of a professional
// org). Deterministic account state; no retry.
const FORBIDDEN: InvitationBanner = {
  variant: "warn",
  title: "You can't send this invitation",
  body: "Your account isn't permitted to invite clinicians into this organization.",
};

// Translate a single 422 detail item into our own (PHI-free) field message. We
// read the field from the END of `loc` (e.g. ["body","work_email"] → "work_email")
// — never the server's message text.
function applyDetailItem(item: unknown, into: InvitationFieldErrors): void {
  if (!item || typeof item !== "object") return;
  const loc = (item as { loc?: unknown }).loc;
  const field = Array.isArray(loc) && loc.length > 0 ? String(loc[loc.length - 1]) : "";

  if (field === "first_name" && !into.firstName) {
    into.firstName = "Enter the clinician's first name (1–255 characters).";
  } else if (field === "last_name" && !into.lastName) {
    into.lastName = "Enter the clinician's last name (1–255 characters).";
  } else if (field === "work_email" && !into.workEmail) {
    into.workEmail = "Enter a valid work email address.";
  } else if (field === "organization_role_label" && !into.organizationRoleLabel) {
    into.organizationRoleLabel = "Select an organization role.";
  }
}

function parse422Details(details: unknown): InvitationFieldErrors {
  const fieldErrors: InvitationFieldErrors = {};
  if (Array.isArray(details)) {
    for (const item of details) applyDetailItem(item, fieldErrors);
  }
  return fieldErrors;
}

/** Read the structured `reason` off a SEAT-002 error's `details` ({reason, message}). */
function readSeatConflictReason(details: unknown): string | null {
  if (details && typeof details === "object" && "reason" in details) {
    const reason = (details as { reason?: unknown }).reason;
    return typeof reason === "string" ? reason : null;
  }
  return null;
}

/** Map an invitation failure to inline field errors + an optional banner. */
export function toCreateInvitationError(error: unknown): MappedInvitationError {
  if (error instanceof ApiError) {
    // SEAT-001 first — a 409 with a dedicated code that must NOT be swallowed by
    // the generic 409 branch.
    if (error.code === "SEAT-001") {
      return { fieldErrors: {}, banner: NO_SEATS };
    }
    // ORG-015 BEFORE the generic 422 — it IS a 422, but its body is
    // `{reason, message}`, not a Pydantic field-error list, so `parse422Details`
    // finds nothing and the refusal would fall out of the branch below as the
    // generic "Check the invitation details" banner: actionable, but silent
    // about which of four fields is wrong. It has exactly one culprit, so it
    // goes inline on `workEmail` with no banner, pointing the admin straight at
    // the address they can fix.
    //
    // It is inline-ONLY, and that is now what separates it from SEAT-002
    // `duplicate_email`, which lands on the same field and gained a banner in
    // Story 22.4. The difference is whether editing the address can help: here
    // it can — the address is malformed as a sign-in identifier — and there it
    // cannot, because the person already has an account.
    //
    // Body only: the shared module's title belongs to the Edit-member BANNER,
    // and a field already labelled "Work email" does not need it repeated.
    if (error.code === "ORG-015") {
      return { fieldErrors: { workEmail: INVALID_SIGN_IN_EMAIL_BODY }, banner: null };
    }
    if (error.status === 422) {
      const fieldErrors = parse422Details(error.details);
      const handledInline = Object.keys(fieldErrors).length > 0;
      return {
        fieldErrors,
        // Only show a banner when nothing could be attached to a field, so the
        // user is not told "check the details" with no field actually flagged.
        banner: handledInline
          ? null
          : {
              variant: "warn",
              title: "Check the invitation details",
              body: "Please review the fields and try again.",
            },
      };
    }
    // SEAT-002 — a seat-write conflict carrying a machine-readable `reason`. Each
    // reason is a different outcome; do NOT collapse them to "already seated".
    if (error.code === "SEAT-002") {
      const reason = readSeatConflictReason(error.details);
      if (reason === "duplicate_email") {
        // AC16 — the marker on the field AND the banner carrying the route. See
        // `EXISTING_ACCOUNT` above for why this stopped being inline-only.
        return {
          fieldErrors: { workEmail: DUPLICATE_EMAIL_FIELD_MESSAGE },
          banner: EXISTING_ACCOUNT,
        };
      }
      if (reason === "capacity_full") {
        // The pool filled under the admin (concurrent capacity change / race):
        // the same calm no-seats banner + Request-additional-seats CTA as SEAT-001.
        return { fieldErrors: {}, banner: NO_SEATS };
      }
      // already_seated OR an unrecognized SEAT-002 reason → the settled
      // already-seated state (safe default for the seat-conflict family).
      return { fieldErrors: {}, banner: ALREADY_SEATED };
    }
    // ORG-002 — the actor's org claim isn't a professional org; no seat pool.
    if (error.code === "ORG-002") {
      return { fieldErrors: {}, banner: ORG_NOT_READY };
    }
    // Any other 409 (unknown code) — defensive fallback to the already-seated
    // state (the historical seat-conflict default).
    if (error.status === 409) {
      return { fieldErrors: {}, banner: ALREADY_SEATED };
    }
    if (error.status === 429) {
      return { fieldErrors: {}, banner: RATE_LIMITED };
    }
    // ORG-010 BEFORE the generic 403 — it IS a 403, and the coded branch has
    // to win or the contract refusal would be reported as an RBAC one.
    if (error.code === "ORG-010") {
      return { fieldErrors: {}, banner: CONTRACT_INACTIVE };
    }
    if (error.status === 403) {
      return { fieldErrors: {}, banner: FORBIDDEN };
    }
    // Any other coded error falls through to the generic danger banner.
  }
  // NETWORK_ERROR / AuthExpiredError / unknown → generic, no internals leaked.
  return { fieldErrors: {}, banner: GENERIC };
}
